import { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiArrowRight, FiCheck, FiClock, FiCopy, FiLoader } from 'react-icons/fi'
import { formatFiatCryptoPair, formatCrypto, formatFiat } from '../../utils/amountFormatter'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'

const COIN_LIGHT_ICONS = {
  BTC: '₿',
  ETH: 'Ξ',
  USDT: '₮',
  USDC: '◊',
  BNB: '⬡',
  SOL: '◎',
  ADA: '₳',
  XRP: '✕',
  DOT: '●',
  DOGE: 'Ð',
  LTC: 'Ł',
  MATIC: '△',
  AVAX: '▲',
  FTM: '◆',
  ARB: '★',
  SHIB: '🐶',
  BCH: 'Ƀ',
  ATOM: '☈',
  XLM: '✸',
  LINK: '🔗',
  AAVE: '🅰️'
}

const COIN_NAME_PATTERNS = [
  { regex: /bitcoin/i, icon: '₿' },
  { regex: /ethereum/i, icon: 'Ξ' },
  { regex: /tether/i, icon: '₮' },
  { regex: /usd coin|usdc/i, icon: '◊' },
  { regex: /binance/i, icon: '⬡' },
  { regex: /solana/i, icon: '◎' },
  { regex: /cardano/i, icon: '₳' },
  { regex: /ripple|xrp/i, icon: '✕' },
  { regex: /polkadot/i, icon: '●' },
  { regex: /dogecoin/i, icon: 'Ð' },
  { regex: /litecoin/i, icon: 'Ł' },
  { regex: /matic/i, icon: '△' },
  { regex: /avalanche/i, icon: '▲' }
]

const normalizeCryptoSymbol = (coin) => {
  if (!coin) return ''
  if (typeof coin === 'string') return coin.toUpperCase().trim()
  const symbol = coin.symbol || coin.code || coin.currency || coin.ticker || coin.asset || coin.id
  return symbol ? String(symbol).toUpperCase().trim() : ''
}

const normalizeCryptoName = (coin) => {
  if (!coin) return ''
  if (typeof coin === 'string') return coin
  return String(coin.name || coin.fullName || coin.title || coin.asset || '').trim()
}

const getCryptoIcon = (symbol, name) => {
  if (symbol && COIN_LIGHT_ICONS[symbol]) return COIN_LIGHT_ICONS[symbol]
  const normalizedName = (name || '').toLowerCase()
  const patternMatch = COIN_NAME_PATTERNS.find(({ regex }) => regex.test(normalizedName))
  if (patternMatch) return patternMatch.icon
  if (symbol && /^USDT|USDC|BUSD$/i.test(symbol)) return '₮'
  if (symbol && /^BTC$/i.test(symbol)) return '₿'
  if (symbol && /^ETH$/i.test(symbol)) return 'Ξ'
  return '🪙'
}

const GIFT_CARD_OPTION = { value: 'GIFT_CARD', label: 'Gift Card' }

const buildCryptoOption = (coin, index) => {
  const symbol = normalizeCryptoSymbol(coin)
  const name = normalizeCryptoName(coin)
  const label = symbol && symbol === name ? symbol : `${name} (${symbol})`
  return {
    value: symbol,
    label,
    icon: getCryptoIcon(symbol, name)
  }
}

export default function CryptoSection({ website, websiteId, section, sectionFormStyling, useDefaultStyling = true, sectionType = 'crypto-product', customerProfile = null, customerAuthToken = null }) {
  const [activeTab, setActiveTab] = useState('buy')
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    bankName: '',
    accountNumber: '',
    accountName: '',
    cryptoType: 'BTC',
    cryptoAmount: '',
    fiatAmount: '',
    walletAddress: '',
    paymentMethod: 'bank_transfer',
    giftCardName: '',
    giftCardAmount: '',
    giftCardCountry: ''
  })
  const [loading, setLoading] = useState(false)
  const [transaction, setTransaction] = useState(null)
  const [countdown, setCountdown] = useState(null)
  const [walletConfig, setWalletConfig] = useState(null)
  const [configLoading, setConfigLoading] = useState(true)
  const [isFormHovered, setIsFormHovered] = useState(false)
  const [isButtonHovered, setIsButtonHovered] = useState(false)

  useEffect(() => {
    if (!customerProfile) return
    setFormData((prev) => ({
      ...prev,
      fullName: prev.fullName || `${customerProfile.firstName || ''} ${customerProfile.lastName || ''}`.trim(),
      email: prev.email || customerProfile.email || '',
      phone: prev.phone || customerProfile.phone || ''
    }))
  }, [customerProfile])

  const buyRate = section?.content?.buyRate || 0
  const sellRate = section?.content?.sellRate || 0
  const usdtBuyRate = section?.content?.usdtBuyRate || 0
  const usdtSellRate = section?.content?.usdtSellRate || 0
  const giftCardNames = (section?.content?.giftCardNames || [])
    .filter((name) => typeof name === 'string')
    .map((name) => name.trim())
    .filter(Boolean)
  const giftCardWhatsApp = (section?.content?.giftCardWhatsApp || '').trim()
  const giftCardEnabled = section?.content?.giftCardEnabled !== false
  const buyEnabled = section?.content?.buyEnabled !== false
  const sellEnabled = section?.content?.sellEnabled !== false

  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const hoverFormStyle = isFormStyling ? {
    backgroundColor: formColors?.hoverBackgroundColor || formColors?.backgroundColor || DEFAULT_FORM_COLORS.hoverBackgroundColor,
    color: formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor
  } : undefined
  const labelStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const hoverLabelStyle = isFormStyling ? { color: formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor } : undefined
  const textStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const hoverTextStyle = isFormStyling ? { color: formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor } : undefined
  const inputStyle = isFormStyling ? { 
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor,
    borderColor: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' } // Always provide default button styling
  const hoverButtonStyle = isButtonStyling ? {
    backgroundColor: formColors?.hoverButtonBackgroundColor || formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.hoverButtonBackgroundColor,
    color: formColors?.hoverButtonTextColor || formColors?.buttonTextColor || DEFAULT_FORM_COLORS.hoverButtonTextColor
  } : { backgroundColor: '#2563EB', color: '#FFFFFF' }

  // Use crypto-specific rates if configured, fallback to general rates
  const currentBuyRate = formData.cryptoType?.startsWith('USDT') && usdtBuyRate > 0 ? usdtBuyRate : buyRate
  const currentSellRate = formData.cryptoType?.startsWith('USDT') && usdtSellRate > 0 ? usdtSellRate : sellRate

  // Fetch wallet configuration on component mount
  useEffect(() => {
    fetchWalletConfig()
  }, [websiteId])

  const fetchWalletConfig = async () => {
    try {
      setConfigLoading(true)
      console.log('[CryptoSection] Fetching wallet config for website:', websiteId)
      const response = await axios.get(`/crypto/config/public/${websiteId}`)
      console.log('[CryptoSection] Wallet config response:', response.data)
      setWalletConfig(response.data)
    } catch (error) {
      console.error('[CryptoSection] Error fetching wallet config:', error)
      const fallbackWalletSource = website?.cryptoWalletConfig?.walletSource || 'provider'
      const fallbackSupportedCoins = fallbackWalletSource === 'manual'
        ? Object.keys(website?.cryptoWalletConfig?.manualWallets || {})
        : []

      setWalletConfig({
        walletSource: fallbackWalletSource,
        supportedCoins: fallbackSupportedCoins,
        cryptoProvider: website?.paymentSettings?.cryptoPaymentProvider,
        hasProvider: !!website?.paymentSettings?.cryptoProviderApiKey
      })
    } finally {
      setConfigLoading(false)
    }
  }

  // Get supported coins based on wallet source
  const supportedCoins = walletConfig?.supportedCoins || []
  const walletSource = walletConfig?.walletSource || 'manual'
  const showGiftCardOption = giftCardEnabled && giftCardNames.length > 0
  const isBuyAvailable = buyEnabled && supportedCoins.length > 0
  const isSellAvailable = sellEnabled && supportedCoins.length > 0
  const isSectionUnavailable = !isBuyAvailable && !isSellAvailable && !showGiftCardOption

  const allowedCryptoValues = ['BTC', 'USDT (TRC20)', 'USDT (ERC20)']
  const cryptoOptions = supportedCoins.length > 0
    ? supportedCoins
        .filter(symbol => allowedCryptoValues.includes(symbol))
        .map((symbol, index) => buildCryptoOption(symbol, index))
    : []

  const cryptoSelectOptions = [
    ...cryptoOptions,
    ...(showGiftCardOption ? [GIFT_CARD_OPTION] : [])
  ]

  useEffect(() => {
    if (cryptoOptions.length > 0 && formData.cryptoType !== GIFT_CARD_OPTION.value && !cryptoOptions.some(option => option.value === formData.cryptoType)) {
      setFormData(prev => ({ ...prev, cryptoType: cryptoOptions[0].value }))
    }
  }, [cryptoOptions, formData.cryptoType])

  // Debug logging
  useEffect(() => {
    if (supportedCoins && supportedCoins.length > 0) {
      console.log(`[CryptoSection] Using ${supportedCoins.length} supported coins from ${walletSource} source:`, supportedCoins.slice(0, 5))
      console.log(`[CryptoSection] Mapped cryptoOptions (${cryptoOptions.length} valid):`, cryptoOptions.slice(0, 5))
    } else {
      console.log('[CryptoSection] No supported coins configured - crypto options will be empty')
    }
  }, [supportedCoins, cryptoOptions, walletSource])

  useEffect(() => {
    if (activeTab === 'giftcard' && !showGiftCardOption) {
      if (isBuyAvailable) setActiveTab('buy')
      else if (isSellAvailable) setActiveTab('sell')
    }
  }, [showGiftCardOption, isBuyAvailable, isSellAvailable, activeTab])

  useEffect(() => {
    if (showGiftCardOption && !isBuyAvailable && !isSellAvailable && activeTab !== 'giftcard') {
      setActiveTab('giftcard')
    }
  }, [showGiftCardOption, isBuyAvailable, isSellAvailable, activeTab])

  const handleInputChange = (e) => {
    const { name, value } = e.target

    if (name === 'cryptoType') {
      if (value === GIFT_CARD_OPTION.value) {
        setActiveTab('giftcard')
        setFormData(prev => ({ ...prev, cryptoType: value }))
        return
      }
      if (activeTab === 'giftcard') {
        setActiveTab('buy')
      }
    }

    if (name === 'cryptoAmount' && activeTab === 'buy') {
      const cryptoAmountValue = value === '' ? '' : value
      const parsedCryptoAmount = parseFloat(cryptoAmountValue)
      const fiatAmount = !isNaN(parsedCryptoAmount) && currentBuyRate > 0
        ? (parsedCryptoAmount * currentBuyRate).toFixed(2)
        : ''

      setFormData(prev => ({
        ...prev,
        cryptoAmount: cryptoAmountValue,
        fiatAmount: fiatAmount
      }))
      return
    }

    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate tab availability
    if (activeTab === 'buy' && !isBuyAvailable) {
      toast.error('Buy functionality is not available.')
      return
    }
    if (activeTab === 'sell' && !isSellAvailable) {
      toast.error('Sell functionality is not available.')
      return
    }

    // Validate form data
    if (!formData.fullName.trim()) {
      toast.error('Please enter your full name')
      return
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email address')
      return
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number')
      return
    }

    if (activeTab === 'buy') {
      if (!formData.cryptoAmount || parseFloat(formData.cryptoAmount) <= 0) {
        toast.error('Please enter a valid crypto amount')
        return
      }
      if (!formData.walletAddress.trim()) {
        toast.error('Please enter your wallet address')
        return
      }
      if (currentBuyRate <= 0) {
        toast.error('Buy rate is not configured. Please try again later.')
        return
      }
    } else if (activeTab === 'sell') {
      if (!formData.bankName.trim()) {
        toast.error('Please enter your bank name')
        return
      }
      if (!formData.accountNumber.trim()) {
        toast.error('Please enter your account number')
        return
      }
      if (!formData.accountName.trim()) {
        toast.error('Please enter your account name')
        return
      }
      if (!formData.cryptoAmount || parseFloat(formData.cryptoAmount) <= 0) {
        toast.error('Please enter a valid crypto amount')
        return
      }
      // Add minimal amount validation
      const cryptoAmount = parseFloat(formData.cryptoAmount)
      if (formData.cryptoType === 'BTC' && cryptoAmount < 0.001) {
        toast.error('Minimum BTC amount is 0.001 BTC')
        return
      }
      if (formData.cryptoType.startsWith('USDT') && cryptoAmount < 1) {
        toast.error('Minimum USDT amount is 1 USDT')
        return
      }
    } else if (activeTab === 'giftcard') {
      if (!giftCardWhatsApp) {
        toast.error('Gift card WhatsApp number is not configured.')
        return
      }
      if (!formData.giftCardName) {
        toast.error('Please select a gift card.')
        return
      }
      if (!formData.giftCardAmount || parseFloat(formData.giftCardAmount) <= 0) {
        toast.error('Please enter the amount you want to send.')
        return
      }
      if (!formData.phone.trim()) {
        toast.error('Please enter your phone number.')
        return
      }
      if (!formData.giftCardCountry.trim()) {
        toast.error('Please enter your country.')
        return
      }

      const whatsappNumber = giftCardWhatsApp.replace(/\D/g, '')
      if (!whatsappNumber) {
        toast.error('Configured WhatsApp number is invalid.')
        return
      }

      const message = `Gift Card Order Details:

• Gift Card Name: ${formData.giftCardName}
• Amount to Send: $${formData.giftCardAmount}
• Phone Number: ${formData.phone}
• Country: ${formData.giftCardCountry}
• Customer Name: ${formData.fullName}
• Email: ${formData.email}`
      setLoading(true)
      window.location.href = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`
      return
    }

    if (activeTab !== 'giftcard') {
      if (cryptoOptions.length === 0) {
        toast.error('No supported cryptocurrencies are configured for this website.')
        return
      }
      if (!cryptoOptions.some(option => option.value === formData.cryptoType)) {
        toast.error('Please select a valid cryptocurrency')
        return
      }

      setLoading(true)

      try {

      const payload = {
        websiteId,
        type: activeTab,
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        cryptoType: formData.cryptoType
      }

      if (activeTab === 'buy') {
        payload.cryptoAmount = parseFloat(formData.cryptoAmount)
        payload.fiatAmount = parseFloat((payload.cryptoAmount * currentBuyRate).toFixed(2))
        payload.walletAddress = formData.walletAddress.trim()
        payload.paymentMethod = formData.paymentMethod
      } else {
        payload.bankName = formData.bankName.trim()
        payload.accountNumber = formData.accountNumber.trim()
        payload.accountName = formData.accountName.trim()
        payload.cryptoAmount = parseFloat(formData.cryptoAmount)
        payload.fiatAmount = parseFloat((payload.cryptoAmount * currentSellRate).toFixed(2))
      }

      const response = await axios.post(
        '/crypto/create',
        payload,
        customerAuthToken ? { headers: { Authorization: `Bearer ${customerAuthToken}` } } : undefined
      )
      setTransaction(response.data.transaction)

      // Start countdown if rate is locked
      if (response.data.transaction.rateLockedUntil) {
        startCountdown(new Date(response.data.transaction.rateLockedUntil))
      }

      toast.success('Transaction created successfully!')
    } catch (error) {
      console.error('Transaction creation error:', error)
      const errorMessage = error.response?.data?.details || error.response?.data?.message || 'Failed to create transaction'
      console.error('Error details:', errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  }

  const startCountdown = (endTime) => {
    const interval = setInterval(() => {
      const now = new Date()
      const diff = endTime - now

      if (diff <= 0) {
        clearInterval(interval)
        setCountdown(null)
        toast.error('Exchange rate lock expired. Please create a new transaction.')
        setTransaction(null)
      } else {
        const minutes = Math.floor(diff / 60000)
        const seconds = Math.floor((diff % 60000) / 1000)
        setCountdown(`${minutes}:${seconds.toString().padStart(2, '0')}`)
      }
    }, 1000)
  }

  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      bankName: '',
      accountNumber: '',
      accountName: '',
      cryptoType: 'BTC',
      cryptoAmount: '',
      fiatAmount: '',
      walletAddress: '',
      paymentMethod: 'bank_transfer',
      giftCardName: '',
      giftCardAmount: '',
      giftCardCountry: ''
    })
    setTransaction(null)
    setCountdown(null)
  }

  if (transaction) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">✅</div>
          <h2 className="text-2xl font-bold" style={textStyle}>Transaction Created Successfully!</h2>
          <p style={textStyle}>Order ID: <span className="font-mono font-bold">{transaction.orderId}</span></p>
          {countdown && (
            <p className="text-sm text-orange-600 mt-2">
              Rate locked for: <span className="font-bold">{countdown}</span>
            </p>
          )}
        </div>

        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Transaction Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Type:</span>
              <span className={`ml-2 px-2 py-1 rounded text-xs font-bold ${transaction.type === 'buy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {transaction.type.toUpperCase()}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Status:</span>
              <span className={`ml-2 px-2 py-1 rounded text-xs font-bold ${transaction.status === 'awaiting_payment' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                {transaction.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Crypto Amount:</span>
              <span className="ml-2 font-mono">{formatCrypto(transaction.cryptoAmount, transaction.cryptoType)}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Fiat Amount:</span>
              <span className="ml-2 font-mono">₦{transaction.fiatAmount?.toLocaleString()}</span>
            </div>
            <div className="md:col-span-2">
              <span className="font-medium text-gray-700">Exchange Rate:</span>
              <span className="ml-2">₦{transaction.appliedRate?.toFixed(2)} per {transaction.cryptoType}</span>
            </div>
          </div>
        </div>

        {transaction.type === 'buy' && transaction.bankDetails && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4 text-blue-900">Payment Instructions</h3>
            <p className="text-blue-800 mb-4">
              Please transfer the fiat amount to the following bank account within the rate lock period:
            </p>
            <div className="bg-white rounded p-4 border">
              <div className="grid grid-cols-1 gap-3">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Bank Name:</span>
                  <span className="font-mono">{transaction.bankDetails.bankName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Account Name:</span>
                  <span className="font-mono">{transaction.bankDetails.accountName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Account Number:</span>
                  <span className="font-mono">{transaction.bankDetails.accountNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Amount to Pay:</span>
                  <span className="font-mono font-bold text-green-600">₦{transaction.fiatAmount?.toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => copyToClipboard(transaction.bankDetails.accountNumber)}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <FiCopy /> Copy Account Number
              </button>
              <button
                onClick={() => copyToClipboard(`₦${transaction.fiatAmount?.toLocaleString()}`)}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <FiCopy /> Copy Amount
              </button>
            </div>
          </div>
        )}

        {transaction.type === 'sell' && transaction.walletAddress && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4 text-red-900">Send Crypto Instructions</h3>
            <p className="text-red-800 mb-4">
              Please send the crypto amount to the following wallet address:
            </p>
            <div className="bg-white rounded p-4 border mb-4">
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <span className="font-medium text-gray-700">Wallet Address:</span>
                  <div className="mt-1 font-mono text-sm break-all bg-gray-50 p-2 rounded">
                    {transaction.walletAddress}
                  </div>
                </div>
                {transaction.walletNetwork && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Network:</span>
                    <span className="font-mono">{transaction.walletNetwork}</span>
                  </div>
                )}
                {transaction.walletTag && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Tag/Memo:</span>
                    <span className="font-mono">{transaction.walletTag}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Amount to Send:</span>
                  <span className="font-mono font-bold text-red-600">{formatCrypto(transaction.cryptoAmount, transaction.cryptoType)}</span>
                </div>
              </div>
            </div>
            {transaction.qrCode && (
              <div className="text-center mb-4">
                <p className="text-sm mb-2" style={textStyle}>Scan QR Code:</p>
                <img
                  src={transaction.qrCode}
                  alt="Wallet QR Code"
                  className="mx-auto max-w-48 h-auto border rounded"
                />
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => copyToClipboard(transaction.walletAddress)}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              >
                <FiCopy /> Copy Address
              </button>
              <button
                onClick={() => copyToClipboard(formatCrypto(transaction.cryptoAmount, transaction.cryptoType))}
                className="flex-1 bg-orange-600 text-white py-2 px-4 rounded hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
              >
                <FiCopy /> Copy Amount
              </button>
            </div>
          </div>
        )}

        <div className="text-center">
          <button
            onClick={resetForm}
            className="bg-gray-600 text-white py-2 px-6 rounded hover:bg-gray-700 transition-colors"
          >
            Create New Transaction
          </button>
        </div>

        <div className="mt-6 text-xs text-gray-500 text-center">
          <p>Keep this information safe. You will receive email confirmations for all updates.</p>
        </div>
      </div>
    )
  } else {
    return (
    <div 
      className="max-w-2xl mx-auto rounded-lg shadow-lg p-8 transition-colors duration-200" 
      style={isFormHovered && hoverFormStyle ? hoverFormStyle : (formStyle || { backgroundColor: '#ffffff' })}
      onMouseEnter={() => setIsFormHovered(true)}
      onMouseLeave={() => setIsFormHovered(false)}
    >
      {/* Exchange Rates Display */}
      {(buyRate > 0 || sellRate > 0 || usdtBuyRate > 0 || usdtSellRate > 0) && (
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3 text-center" style={textStyle}>Current Exchange Rates</h3>
          <div className="flex justify-center gap-6 flex-wrap">
            {buyRate > 0 && (
              <div className="text-center">
                <div className="text-sm" style={textStyle}>BTC Buy Rate</div>
                <div className="text-2xl font-bold text-green-600">₦{buyRate.toLocaleString()}</div>
                <div className="text-xs text-gray-500">per BTC</div>
              </div>
            )}
            {sellRate > 0 && (
              <div className="text-center">
                <div className="text-sm" style={textStyle}>BTC Sell Rate</div>
                <div className="text-2xl font-bold text-red-600">₦{sellRate.toLocaleString()}</div>
                <div className="text-xs text-gray-500">per BTC</div>
              </div>
            )}
            {usdtBuyRate > 0 && (
              <div className="text-center">
                <div className="text-sm" style={textStyle}>USDT Buy Rate</div>
                <div className="text-2xl font-bold text-blue-600">₦{usdtBuyRate.toLocaleString()}</div>
                <div className="text-xs text-gray-500">per USDT</div>
              </div>
            )}
            {usdtSellRate > 0 && (
              <div className="text-center">
                <div className="text-sm" style={textStyle}>USDT Sell Rate</div>
                <div className="text-2xl font-bold text-purple-600">₦{usdtSellRate.toLocaleString()}</div>
                <div className="text-xs text-gray-500">per USDT</div>
              </div>
            )}
          </div>
        </div>
      )}

      {isSectionUnavailable && (
        <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-6 text-center">
          <div className="text-4xl mb-3">⚠️</div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Crypto Services Unavailable</h3>
          <p className="text-gray-600">
            Both buy and sell crypto services are currently turned off. Exchange rates remain visible for reference.
          </p>
        </div>
      )}

      {/* Tab Selector - Only show available tabs */}
      {!isSectionUnavailable && (isBuyAvailable || isSellAvailable || showGiftCardOption) && (
        <div className="flex mb-6 bg-gray-100 rounded-lg p-1 flex-wrap">
          {isBuyAvailable && (
            <button
              onClick={() => setActiveTab('buy')}
              className={`flex-1 min-w-[120px] py-2 px-4 rounded-md font-medium transition-colors ${
                activeTab === 'buy' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              🟢 BUY CRYPTO
            </button>
          )}
          {isSellAvailable && (
            <button
              onClick={() => setActiveTab('sell')}
              className={`flex-1 min-w-[120px] py-2 px-4 rounded-md font-medium transition-colors ${
                activeTab === 'sell' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              🔴 SELL CRYPTO
            </button>
          )}
          {showGiftCardOption && (
            <button
              onClick={() => setActiveTab('giftcard')}
              className={`flex-1 min-w-[140px] py-2 px-4 rounded-md font-medium transition-colors ${
                activeTab === 'giftcard' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              🎁 GIFT CARD
            </button>
          )}
        </div>
      )}

      {/* Unavailable Messages */}
      {!isSectionUnavailable && activeTab === 'buy' && !isBuyAvailable && (
        <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-900">
          <p className="font-semibold">Crypto buy is currently unavailable.</p>
          <p>
            {!buyEnabled
              ? 'Buy functionality has been disabled.'
              : 'A buy rate must be configured and a supported crypto wallet must be available.'}
          </p>
        </div>
      )}

      {!isSectionUnavailable && activeTab === 'sell' && !isSellAvailable && (
        <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-900">
          <p className="font-semibold">Crypto sell is currently unavailable.</p>
          <p>
            {!sellEnabled
              ? 'Sell functionality has been disabled.'
              : 'A sell rate must be set in the crypto section.'}
          </p>
        </div>
      )}

      {!isSectionUnavailable && configLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading crypto configuration...</p>
          </div>
        </div>
      )}

      {!isSectionUnavailable && !configLoading && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={labelStyle}>Full Name</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={labelStyle}>Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email address"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={labelStyle}>Phone Number</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your phone number"
            />
          </div>

          {activeTab === 'buy' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>Cryptocurrency</label>
                  <select
                    name="cryptoType"
                    value={formData.cryptoType || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    {cryptoSelectOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>Crypto Amount</label>
                  <input
                    type="number"
                    name="cryptoAmount"
                    value={formData.cryptoAmount}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.000001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                  {formData.cryptoAmount && currentBuyRate > 0 && (
                    <div className="mt-1 text-sm text-green-600">
                      Amount to pay: ₦{(parseFloat(formData.cryptoAmount) * currentBuyRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={labelStyle}>Your Wallet Address</label>
                <input
                  type="text"
                  name="walletAddress"
                  value={formData.walletAddress}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your crypto wallet address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={labelStyle}>Payment Method</label>
                <div className="w-full px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                  Bank Transfer
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Bank transfer is the only available payment method for crypto purchases.
                </p>
              </div>
            </>
          ) : activeTab === 'sell' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>Bank Name</label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your bank name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>Account Number</label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your account number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={labelStyle}>Account Name</label>
                <input
                  type="text"
                  name="accountName"
                  value={formData.accountName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your account name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>Cryptocurrency</label>
                  <select
                    name="cryptoType"
                    value={formData.cryptoType || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    {cryptoSelectOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>Crypto Amount</label>
                  <input
                    type="number"
                    name="cryptoAmount"
                    value={formData.cryptoAmount}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.000001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                  {formData.cryptoAmount && currentSellRate > 0 && (
                    <div className="mt-1 text-sm text-red-600">
                      You will receive: ₦{(parseFloat(formData.cryptoAmount) * currentSellRate).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>Gift Card Name</label>
                  <select
                    name="giftCardName"
                    value={formData.giftCardName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="" disabled>Select a gift card</option>
                    {giftCardNames.map((name, index) => (
                      <option key={`${name}-${index}`} value={name}>{name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>You Send (Amount)</label>
                  <input
                    type="number"
                    name="giftCardAmount"
                    value={formData.giftCardAmount}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={labelStyle}>Country</label>
                  <input
                    type="text"
                    name="giftCardCountry"
                    value={formData.giftCardCountry}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your country"
                  />
                </div>
              </div>

              <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-700">
                <p>
                  After submitting, you will be redirected to WhatsApp with your gift card request pre-filled.
                </p>
              </div>
            </>
          )}

        <button
          type="submit"
          disabled={loading || (activeTab !== 'giftcard' && configLoading) || (activeTab === 'buy' && !isBuyAvailable) || (activeTab === 'sell' && !isSellAvailable)}
          className="w-full py-3 px-6 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 duration-200"
          style={isButtonHovered && hoverButtonStyle ? hoverButtonStyle : buttonStyle}
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}
        >
          {loading ? (
            <>
              <FiLoader className="w-4 h-4 animate-spin" />
              Creating Transaction...
            </>
          ) : (
            'Submit'
          )}
        </button>
      </form>
      )}

      <div className="mt-6 text-xs text-gray-500 text-center">
        <p>Transactions are processed securely. Please ensure all information is accurate.</p>
      </div>
    </div>
  )
  }
}
